import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import "./FinanceReport.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

const getAuthConfig = () => {
  const token = localStorage.getItem("token") || "";
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const STATUS_META = {
  PENDING_APPROVAL: { label: "Pending Approval", cls: "pending" },
  APPROVED: { label: "Approved (Not Paid)", cls: "approved" },
  PAID: { label: "Paid", cls: "paid" },
  REJECTED: { label: "Rejected", cls: "rejected" },
};

const STATUS_KEYS = Object.keys(STATUS_META);

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/* ---------- Date helpers ---------- */

const pad = (n) => String(n).padStart(2, "0");

const toYMD = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseDate = (value) => {
  if (!value) return null;
  const text = String(value).trim();
  const date = /^\d{4}-\d{2}-\d{2}$/.test(text)
    ? new Date(`${text}T00:00:00`)
    : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatDate = (date) => {
  if (!date) return "-";
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const formatINR = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Indian financial year: April to March.
const fyOf = (date) =>
  date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;

const quarterOf = (date) => {
  const month = date.getMonth();
  if (month >= 3 && month <= 5) return 1;
  if (month >= 6 && month <= 8) return 2;
  if (month >= 9 && month <= 11) return 3;
  return 4;
};

const fyLabel = (fy) => `FY ${fy}-${String(fy + 1).slice(2)}`;

// The date that places an entry in a period:
// PAID entries use the actual payment date, others use the request date.
const entryDateOf = (row) => {
  if (row.status === "PAID") {
    return (
      parseDate(row.paymentDate) ||
      parseDate(row.date) ||
      parseDate(row.createdAt)
    );
  }
  return parseDate(row.date) || parseDate(row.createdAt);
};

// Actual payment date of a PAID entry. Older paid entries have no
// paymentDate saved, so the request date is shown instead.
const paidDateInfo = (row) => {
  if (row.status !== "PAID") return { date: null, fallback: false };
  const actual = parseDate(row.paymentDate);
  if (actual) return { date: actual, fallback: false };
  return {
    date: parseDate(row.date) || parseDate(row.createdAt),
    fallback: true,
  };
};

const getRange = (type, dayValue, fy, quarter) => {
  if (type === "daily") {
    const start = parseDate(dayValue) || new Date();
    return {
      start,
      end: addDays(start, 1),
      label: formatDate(start),
      groupBy: "day",
    };
  }

  if (type === "weekly") {
    const picked = parseDate(dayValue) || new Date();
    const dayOfWeek = (picked.getDay() + 6) % 7; // Monday = 0
    const start = addDays(picked, -dayOfWeek);
    const end = addDays(start, 7);
    return {
      start,
      end,
      label: `${formatDate(start)} to ${formatDate(addDays(end, -1))}`,
      groupBy: "day",
    };
  }

  if (type === "quarterly") {
    const startMonth = [3, 6, 9, 0][quarter - 1];
    const year = quarter === 4 ? fy + 1 : fy;
    const start = new Date(year, startMonth, 1);
    const end = new Date(year, startMonth + 3, 1);
    return {
      start,
      end,
      label: `Q${quarter} ${fyLabel(fy)}`,
      groupBy: "month",
    };
  }

  const start = new Date(fy, 3, 1);
  const end = new Date(fy + 1, 3, 1);
  return { start, end, label: fyLabel(fy), groupBy: "month" };
};

const emptyTotals = () =>
  STATUS_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: { count: 0, amount: 0 } }),
    {},
  );

/* ---------- Component ---------- */

const FinanceReport = () => {
  const navigate = useNavigate();

  let loggedInUser = {};
  try {
    loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    loggedInUser = {};
  }

  const userRole = String(
    loggedInUser?.role ||
      loggedInUser?.roleName ||
      localStorage.getItem("userRole") ||
      "",
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  const canView = ["admin", "head_accountant", "accountant"].includes(userRole);

  const today = new Date();

  const [periodType, setPeriodType] = useState("daily");
  const [dayValue, setDayValue] = useState(toYMD(today));
  const [fy, setFy] = useState(fyOf(today));
  const [quarter, setQuarter] = useState(quarterOf(today));
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Only the financial years in which entries exist (latest first).
  const fyOptions = useMemo(() => {
    const years = new Set();

    rows.forEach((row) => {
      if (!STATUS_META[row.status]) return;
      const entryDate = entryDateOf(row);
      if (entryDate) years.add(fyOf(entryDate));
    });

    if (years.size === 0) years.add(fyOf(new Date()));

    return Array.from(years).sort((a, b) => b - a);
  }, [rows]);

  // Select the latest year that has entries when the selected year has none.
  useEffect(() => {
    if (!fyOptions.includes(fy)) setFy(fyOptions[0]);
  }, [fyOptions, fy]);

  useEffect(() => {
    if (!canView) return;

    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(
          `${BASE_URL}/api/vendor-payment-requests`,
          getAuthConfig(),
        );
        if (!active) return;
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!active) return;
        console.error("Payment report fetch error:", {
          status: err?.response?.status,
          data: err?.response?.data,
          message: err?.message,
        });
        setRows([]);
        setError(
          err?.response?.data?.message ||
            "Could not load payment entries. Please try again.",
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [canView]);

  const range = useMemo(
    () => getRange(periodType, dayValue, fy, quarter),
    [periodType, dayValue, fy, quarter],
  );

  // All entries that fall inside the selected period.
  const periodRows = useMemo(() => {
    return rows
      .map((row) => ({ row, entryDate: entryDateOf(row) }))
      .filter(
        ({ row, entryDate }) =>
          entryDate &&
          entryDate >= range.start &&
          entryDate < range.end &&
          STATUS_META[row.status],
      )
      .sort((a, b) => b.entryDate - a.entryDate);
  }, [rows, range]);

  const totals = useMemo(() => {
    const result = emptyTotals();
    periodRows.forEach(({ row }) => {
      result[row.status].count += 1;
      result[row.status].amount += Number(row.paymentAmt || 0);
    });
    return result;
  }, [periodRows]);

  const sentTotals = useMemo(
    () =>
      STATUS_KEYS.reduce(
        (acc, key) => ({
          count: acc.count + totals[key].count,
          amount: acc.amount + totals[key].amount,
        }),
        { count: 0, amount: 0 },
      ),
    [totals],
  );

  const visibleRows = useMemo(
    () =>
      statusFilter === "ALL"
        ? periodRows
        : periodRows.filter(({ row }) => row.status === statusFilter),
    [periodRows, statusFilter],
  );

  const breakdown = useMemo(() => {
    const groups = new Map();

    periodRows.forEach(({ row, entryDate }) => {
      const key =
        range.groupBy === "day"
          ? toYMD(entryDate)
          : `${entryDate.getFullYear()}-${pad(entryDate.getMonth() + 1)}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label:
            range.groupBy === "day"
              ? formatDate(entryDate)
              : `${MONTHS[entryDate.getMonth()]} ${entryDate.getFullYear()}`,
          count: 0,
          totals: emptyTotals(),
        });
      }

      const group = groups.get(key);
      group.count += 1;
      group.totals[row.status].count += 1;
      group.totals[row.status].amount += Number(row.paymentAmt || 0);
    });

    return Array.from(groups.values()).sort((a, b) =>
      a.key.localeCompare(b.key),
    );
  }, [periodRows, range.groupBy]);

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Payment Report");

    sheet.addRow(["Payment Report"]);
    sheet.addRow(["Period", range.label]);
    sheet.addRow([
      "Status Filter",
      statusFilter === "ALL" ? "All" : STATUS_META[statusFilter].label,
    ]);
    sheet.addRow([]);

    const summaryHeader = sheet.addRow(["Status", "Entries", "Amount"]);
    summaryHeader.font = { bold: true };
    sheet.addRow(["Sent (All)", sentTotals.count, sentTotals.amount]);
    STATUS_KEYS.forEach((key) => {
      sheet.addRow([
        STATUS_META[key].label,
        totals[key].count,
        totals[key].amount,
      ]);
    });
    sheet.addRow([]);

    const header = sheet.addRow([
      "Sr No",
      "Date",
      "Project",
      "Vendor Name",
      "Vendor Code",
      "Work Type",
      "Payment Mode",
      "GST Invoice",
      "Amount",
      "Status",
      "Request Date",
      "Actual Payment Date",
    ]);
    header.font = { bold: true };

    visibleRows.forEach(({ row, entryDate }, index) => {
      sheet.addRow([
        index + 1,
        formatDate(entryDate),
        row.projectName || "",
        row.vendorName || "",
        row.vendorCode || "",
        row.workType || "",
        row.mode || "",
        row.gstInvoice || "",
        Number(row.paymentAmt || 0),
        STATUS_META[row.status]?.label || row.status,
        formatDate(parseDate(row.date)),
        row.status === "PAID" ? formatDate(paidDateInfo(row).date) : "",
      ]);
    });

    sheet.columns.forEach((column) => {
      column.width = 22;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `PaymentReport_${range.label.replace(/[^a-z0-9_-]/gi, "_")}.xlsx`,
    );
  };

  if (!canView) {
    return (
      <div className="fr-no-access">
        You do not have permission to view the payment report.
      </div>
    );
  }

  return (
    <div className="fr-page">
      <header className="fr-header">
        <button
          type="button"
          className="fr-btn fr-btn-back"
          onClick={() => navigate("/Finance")}
        >
          ← Back
        </button>

        <div className="fr-header-center">
          <div className="fr-eyebrow">FINANCE</div>
          <h2>Payment Report</h2>
          <p>{range.label}</p>
        </div>

        <button
          type="button"
          className="fr-btn fr-btn-export"
          onClick={exportExcel}
          disabled={!visibleRows.length}
        >
          Export to Excel
        </button>
      </header>

      {/* Period buttons + filters + status buttons: ONE row */}
      <section className="fr-card fr-card-toolbar">
        <div className="fr-toolbar">
          <div className="fr-tabs" role="tablist">
            {[
              ["daily", "Daily"],
              ["weekly", "Weekly"],
              ["quarterly", "Quarterly"],
              ["fy", "Financial Year"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={periodType === key}
                className={`fr-tab${periodType === key ? " active" : ""}`}
                onClick={() => setPeriodType(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="fr-filters">
            {(periodType === "daily" || periodType === "weekly") && (
              <label className="fr-field">
                <span>
                  {periodType === "daily" ? "Select Date" : "Any Date in Week"}
                </span>
                <input
                  type="date"
                  className="fr-input"
                  value={dayValue}
                  onChange={(e) => setDayValue(e.target.value)}
                />
              </label>
            )}

            {(periodType === "quarterly" || periodType === "fy") && (
              <label className="fr-field">
                <span>Financial Year</span>
                <select
                  className="fr-input"
                  value={fy}
                  onChange={(e) => setFy(Number(e.target.value))}
                >
                  {fyOptions.map((year) => (
                    <option key={year} value={year}>
                      {fyLabel(year)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {periodType === "quarterly" && (
              <label className="fr-field">
                <span>Quarter</span>
                <select
                  className="fr-input"
                  value={quarter}
                  onChange={(e) => setQuarter(Number(e.target.value))}
                >
                  <option value={1}>Q1 (Apr - Jun)</option>
                  <option value={2}>Q2 (Jul - Sep)</option>
                  <option value={3}>Q3 (Oct - Dec)</option>
                  <option value={4}>Q4 (Jan - Mar)</option>
                </select>
              </label>
            )}
          </div>

          <div className="fr-status-row">
            <span className="fr-status-row-label">Status</span>

            <button
              type="button"
              className={`fr-chip fr-chip-all${statusFilter === "ALL" ? " active" : ""}`}
              onClick={() => setStatusFilter("ALL")}
            >
              All
              <span className="fr-chip-count">{sentTotals.count}</span>
            </button>

            {STATUS_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={`fr-chip fr-chip-${STATUS_META[key].cls}${statusFilter === key ? " active" : ""}`}
                onClick={() => setStatusFilter(key)}
              >
                {STATUS_META[key].label}
                <span className="fr-chip-count">{totals[key].count}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {error && <div className="fr-error">{error}</div>}

      {/* Summary cards: label on the left, amount on the right */}
      <section className="fr-cards">
        <button
          type="button"
          className={`fr-stat fr-stat-sent${statusFilter === "ALL" ? " active" : ""}`}
          onClick={() => setStatusFilter("ALL")}
        >
          <div className="fr-stat-top">
            <span className="fr-stat-label">Sent (All Requests)</span>
            <strong className="fr-stat-amount">
              ₹ {formatINR(sentTotals.amount)}
            </strong>
          </div>
          <span className="fr-stat-count">{sentTotals.count} entries</span>
        </button>

        {STATUS_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={`fr-stat fr-stat-${STATUS_META[key].cls}${statusFilter === key ? " active" : ""}`}
            onClick={() => setStatusFilter(key)}
          >
            <div className="fr-stat-top">
              <span className="fr-stat-label">{STATUS_META[key].label}</span>
              <strong className="fr-stat-amount">
                ₹ {formatINR(totals[key].amount)}
              </strong>
            </div>
            <span className="fr-stat-count">{totals[key].count} entries</span>
          </button>
        ))}
      </section>

      {/* Breakdown */}
      <section className="fr-card">
        <div className="fr-card-head">
          <h3>
            {range.groupBy === "day" ? "Day-wise" : "Month-wise"} Breakdown
          </h3>
          <div className="fr-card-side">
            <div className="fr-eyebrow">SUMMARY</div>
          </div>
        </div>

        <div className="fr-table-scroll">
          <table className="fr-table fr-table-summary">
            <thead>
              <tr>
                <th>{range.groupBy === "day" ? "Date" : "Month"}</th>
                <th className="fr-center">Entries</th>
                <th className="fr-num">Pending Approval</th>
                <th className="fr-num">Approved (Not Paid)</th>
                <th className="fr-num">Paid</th>
                <th className="fr-num">Rejected</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="fr-empty">
                    Loading...
                  </td>
                </tr>
              ) : breakdown.length === 0 ? (
                <tr>
                  <td colSpan="6" className="fr-empty">
                    No entries found for this period.
                  </td>
                </tr>
              ) : (
                breakdown.map((group) => (
                  <tr key={group.key}>
                    <td className="fr-strong">{group.label}</td>
                    <td className="fr-center">{group.count}</td>
                    {STATUS_KEYS.map((key) => (
                      <td key={key} className="fr-num">
                        {group.totals[key].count > 0
                          ? formatINR(group.totals[key].amount)
                          : "-"}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            {breakdown.length > 0 && (
              <tfoot>
                <tr>
                  <td>TOTAL</td>
                  <td className="fr-center">{sentTotals.count}</td>
                  {STATUS_KEYS.map((key) => (
                    <td key={key} className="fr-num">
                      {formatINR(totals[key].amount)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      {/* Entries */}
      <section className="fr-card">
        <div className="fr-card-head">
          <h3>
            {statusFilter === "ALL"
              ? "All Payment Entries"
              : STATUS_META[statusFilter].label}
          </h3>
          <div className="fr-card-side">
            <div className="fr-eyebrow">ENTRIES</div>
            <div className="fr-pill">{visibleRows.length} entries</div>
          </div>
        </div>

        <div className="fr-table-scroll">
          <table className="fr-table fr-table-entries">
            <thead>
              <tr>
                <th className="fr-center">Sr No</th>
                <th>Date</th>
                <th>Project</th>
                <th>Vendor Name</th>
                <th>Vendor Code</th>
                <th>Work Type</th>
                <th>Mode</th>
                <th className="fr-num">Amount</th>
                <th>Status</th>
                <th>Actual Payment Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="fr-empty">
                    Loading...
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="fr-empty">
                    No entries found.
                  </td>
                </tr>
              ) : (
                visibleRows.map(({ row, entryDate }, index) => {
                  const paid = paidDateInfo(row);

                  return (
                    <tr key={row._id || index}>
                      <td className="fr-center">{index + 1}</td>
                      <td>{formatDate(entryDate)}</td>
                      <td>{row.projectName || "-"}</td>
                      <td className="fr-strong">{row.vendorName || "-"}</td>
                      <td>{row.vendorCode || "-"}</td>
                      <td>{row.workType || "-"}</td>
                      <td>{row.mode || "-"}</td>
                      <td className="fr-num fr-strong">
                        {formatINR(row.paymentAmt)}
                      </td>
                      <td>
                        <span
                          className={`fr-status fr-status-${STATUS_META[row.status].cls}`}
                        >
                          {STATUS_META[row.status].label}
                        </span>
                      </td>
                      <td>
                        {paid.date ? (
                          <>
                            {formatDate(paid.date)}
                            {paid.fallback && (
                              <span className="fr-fallback">
                                {" "}
                                (request date)
                              </span>
                            )}
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default FinanceReport;
